import { useEffect, useState } from "react";
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import { Button, Form, Space, Spin, Tabs, Tag, Tooltip } from 'antd';
import Utilisateur from "../../../Models/Utilisateur";
import type { TabsProps } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import SweetAlert from "sweetalert2";
import { Crypter } from "../../../Functions/Security/Crypter";
import { OpeApi } from "../../../Services/ApiOpeLiees";
import axios from "axios";
import { saveAs } from "file-saver";
import Tab1 from "./Tab1";
import { Box, Typography } from "@mui/material";
import dayjs from 'dayjs';
import { useNavigate } from "react-router-dom";
import VerifDay from "../../../Functions/Traitement/VerifDay";
import type { ColumnsType } from 'antd/es/table';

// Referentiels backend (CHAMPEVAL / EVALOPERATEUR)
export interface ChampRef { CODCHAMP: string; LIBCHAMP: string; TYPDON: string; }
export interface AgenceRef { CODAGE: string; LIBELLE: string; }
export interface OperateurRef {
    CODOPECOMP: string; LIBCOMP: string; TYPDON_APPLIQ: string;
    REQUIERT_VAL2: boolean | number; REQUIERT_LISTE: boolean | number;
}

// Ligne unifiee de la table commune : condition simple ou groupe
export interface RegleRow {
    _key: string;
    TYPE: 'CONDITION' | 'GROUPE';
    CODE: string;                 // CODCOND ou CODGRP
    IDGRP?: number;               // groupes uniquement
    LIBELLE: string;
    ACTIVE: boolean;
    EXPRESSION: string;
    raw: any;                     // ligne backend originale (ROWVER inclus)
}

// Arbre de groupe resolu recursivement cote frontend (ReadGroupe)
export interface ElementNode {
    ORDRE: number;
    CODCOND: string | null;
    IDGRPFILS: number | null;
    FILS: GroupeNode | null;
    // Condition inline (sans code) : persiste directement sur l'element
    CHAMPEV?: string | null;
    OPECOMP?: string | null;
    VALEUR1?: string | null;
    VALEUR2?: string | null;
    LISTEVAL?: string | null;
}
export interface GroupeNode {
    IDGRP: number;
    CODGRP: string | null;
    LIBGRP: string | null;
    LOGIQUE: 'ET' | 'OU';
    ACTIVE: boolean;
    ANONYME?: boolean;
    ROWVER?: string;
    ELEMENTS: ElementNode[];
}

export interface SaveResult { ok: boolean; message?: string; detail?: string; }
export interface SimuResult {
    ok: boolean; message?: string;
    resultat?: string;                      // VRAI | FAUX | INEVALUABLE
    detail?: string;                        // references cassees, le cas echeant
    snap?: { EXPR: string; RES: number }[]; // verdict par feuille (1/0/-1)
}

// Requete serveur de la table commune : pagination + filtres cote backend
export interface RegleQuery {
    page: number;
    type: 'TOUS' | 'CONDITION' | 'GROUPE';
    statut: 'TOUS' | 'ACTIVES' | 'INACTIVES';
    search: string;
}

const PAGE_SIZE = 6;
const PROF_MAX_DEFAUT = 3;

// Expression lisible d'une condition simple : "MONTANT > 1000000",
// "MONTANT ENTRE 10 ET 20", "CHAMP DANS (A ; B)" selon l'operateur.
// Declaree en function (hoistee) : importee aussi par Tab1 pour le preview.
export function exprCondition(c: any, ops: OperateurRef[]): string {
    const op = ops.find(o => o.CODOPECOMP === c.OPECOMP);
    const champ = c.CHAMPEV || '?';
    if (op?.REQUIERT_LISTE === true || op?.REQUIERT_LISTE === 1)
        return `${champ} ${c.OPECOMP} (${(c.LISTEVAL || '').split(';').filter(Boolean).join(' ; ')})`;
    if (op?.REQUIERT_VAL2 === true || op?.REQUIERT_VAL2 === 1)
        return `${champ} ${c.OPECOMP} ${c.VALEUR1 ?? '?'} ET ${c.VALEUR2 ?? '?'}`;
    return `${champ} ${c.OPECOMP} ${c.VALEUR1 ?? ''}`;
}

const ConditionsOperations = () => {
    const auth = useAuthUser();
    const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();
    const Navigate = useNavigate();
    const [form] = Form.useForm();

    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    useEffect(() => {
        VerifDay(user.coduti, user.codage, authHeader, Navigate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState<RegleRow[]>([]);
    const [champs, setChamps] = useState<ChampRef[]>([]);
    const [operateurs, setOperateurs] = useState<OperateurRef[]>([]);
    const [agences, setAgences] = useState<AgenceRef[]>([]);
    const [conditions, setConditions] = useState<any[]>([]);
    const [groupes, setGroupes] = useState<any[]>([]);
    const [profMax, setProfMax] = useState(PROF_MAX_DEFAUT);

    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [selectedRule, setSelectedRule] = useState<RegleRow | null>(null);
    const [groupeTree, setGroupeTree] = useState<GroupeNode | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Pagination + filtres pilotés par le backend (ReadRegles)
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<RegleQuery>({ page: 1, type: 'TOUS', statut: 'TOUS', search: '' });

    // Modale builder (detenue par Tab1, pilotee par le parent)
    const [modalOpen, setModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState<RegleRow | null>(null);
    const [editingTree, setEditingTree] = useState<GroupeNode | null>(null);

    const decrypt = (data: any): any => {
        try { return data ? JSON.parse(Crypter.decrypt(data)) : null; }
        catch { return null; }
    };

    // ops est passe en parametre a loadRules : au premier chargement, le
    // state operateurs n'est pas encore commite quand la liste s'execute.
    const loadReferentiels = async (): Promise<{ ops: OperateurRef[] }> => {
        let ops: OperateurRef[] = [];
        try {
            const [ch, op, pa, ag] = await Promise.all([
                axios.get(OpeApi.ReadChampsEval, { headers: { 'Authorization': authHeader() } }),
                axios.get(OpeApi.ReadOperateursEval, { headers: { 'Authorization': authHeader() } }),
                axios.get(OpeApi.ReadParametres, { headers: { 'Authorization': authHeader() } }),
                axios.get(OpeApi.ReadAgences, { headers: { 'Authorization': authHeader() } }),
            ]);
            setChamps(decrypt(ch.data)?.Result?.Table ?? []);
            setAgences(decrypt(ag.data)?.Result?.Table ?? []);
            ops = decrypt(op.data)?.Result?.Table ?? [];
            setOperateurs(ops);
            const params = decrypt(pa.data)?.Result?.Table ?? [];
            const p = params.find((x: any) => x.CODPARAM === 'OPE_GROUPE_PROF_MAX');
            if (p?.VALPARAM) setProfMax(parseInt(p.VALPARAM) || PROF_MAX_DEFAUT);
        } catch (err) {
            console.error('Erreur chargement referentiels:', err);
        }
        return { ops };
    };

    // Catalogue complet (references du constructeur + selecteurs) : non pagine
    const loadCatalogues = async () => {
        try {
            const [c, g] = await Promise.all([
                axios.get(OpeApi.ReadConditions, { headers: { 'Authorization': authHeader() } }),
                axios.get(OpeApi.ReadGroupes, { headers: { 'Authorization': authHeader() } }),
            ]);
            setConditions(decrypt(c.data)?.Result?.Table ?? []);
            setGroupes(decrypt(g.data)?.Result?.Table ?? []);
        } catch (err) {
            console.error('Erreur chargement catalogues:', err);
        }
    };

    // Page de la table commune : pagination/filtres/recherche cote backend.
    const loadRules = async (
        ops: OperateurRef[] = operateurs,
        q: RegleQuery = query
    ): Promise<RegleRow[]> => {
        setLoading(true);
        try {
            const res = await axios.get(OpeApi.ReadRegles, {
                headers: {
                    'Authorization': authHeader(),
                    'PAGE': String(q.page), 'PAGESIZE': String(PAGE_SIZE),
                    'TYPE': q.type, 'STATUT': q.statut, 'SEARCH': q.search,
                }
            });
            const r = decrypt(res.data);
            const rows = r?.Result?.Table ?? [];
            setTotal(r?.Result?.Table1?.[0]?.TOTAL ?? rows.length);
            const merged: RegleRow[] = rows.map((x: any) => ({
                _key: x.TYPE === 'GROUPE' ? `G_${x.IDGRP}` : `C_${x.CODE}`,
                TYPE: x.TYPE,
                CODE: x.CODE,
                IDGRP: x.IDGRP ?? undefined,
                LIBELLE: x.LIBELLE,
                ACTIVE: x.ACTIVE === true || x.ACTIVE === 1,
                EXPRESSION: x.TYPE === 'CONDITION' ? exprCondition(x, ops) : (x.EXPRCANON ?? ''),
                // ReadRegles expose CODE/LIBELLE : alias vers les noms
                // attendus par le formulaire d'edition (CODCOND/LIBCOND)
                raw: x.TYPE === 'CONDITION'
                    ? { ...x, CODCOND: x.CODE, LIBCOND: x.LIBELLE }
                    : x,
            }));
            setRules(merged);
            return merged;
        } catch (err) {
            console.error('Erreur chargement regles:', err);
            setRules([]);
            setTotal(0);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const onQuery = (q: RegleQuery) => {
        setQuery(q);
        void loadRules(operateurs, q);
    };

    // Export : gere par le backend (OpeCatalogue/ExportRegles, pipeline
    // FastReport partage) — le front demande le fichier et le telecharge.
    // Les metadonnees d'edition (agence, banque, utilisateur) sont
    // derivees du jeton cote backend : seuls les filtres transitent.
    const handleExportRegles = async (fmt: string) => {
        try {
            const res = await axios.get(OpeApi.ExportRegles, {
                params: {
                    TYPE: query.type, STATUT: query.statut, SEARCH: query.search,
                    format: fmt.toUpperCase(),
                },
                responseType: 'blob',
                headers: { 'Authorization': authHeader() },
            });
            const mime: Record<string, string> = {
                pdf: 'application/pdf',
                xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                csv: 'text/csv;charset=utf-8',
            };
            const ext = ['pdf', 'xlsx', 'docx', 'csv'].includes(fmt) ? fmt : 'txt';
            saveAs(new Blob([res.data], { type: mime[fmt] ?? 'application/octet-stream' }),
                `regles_${dayjs().format('YYYYMMDD_HHmm')}.${ext}`);
        } catch (e: any) {
            // En cas d'erreur HTTP le corps est un Blob : on le lit pour
            // afficher le message detaille de l'API au lieu de
            // "Request failed with status code 500".
            let detail = e?.message ?? '';
            const data = e?.response?.data;
            if (data instanceof Blob) {
                try {
                    const texte = await data.text();
                    try {
                        const j = JSON.parse(texte);
                        detail = j?.Message ?? j?.message ?? texte;
                    } catch { detail = texte; }
                } catch { /* corps illisible : on garde e.message */ }
            }
            SweetAlert.fire("Erreur", "Export impossible : " + detail, "error");
        }
    };

    useEffect(() => {
        loadReferentiels().then(({ ops }) => { void loadRules(ops); void loadCatalogues(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Charge recursivement l'arbre d'un groupe (elements + sous-groupes resolus)
    const loadGroupeNode = async (idgrp: number, profondeur = 0): Promise<GroupeNode | null> => {
        if (profondeur > profMax) return null;
        try {
            const res = await axios.get(OpeApi.ReadGroupe, {
                headers: { 'IDGRP': String(idgrp), 'Authorization': authHeader() }
            });
            const r = decrypt(res.data);
            const g = r?.Result?.Table?.[0];
            if (r?.StatutCode !== 200 || !g) return null;
            const elems = r.Result.Table1 ?? [];
            const elements: ElementNode[] = [];
            for (const e of elems) {
                elements.push({
                    ORDRE: e.ORDRE,
                    CODCOND: e.CODCOND ?? null,
                    IDGRPFILS: e.IDGRPFILS ?? null,
                    FILS: e.IDGRPFILS ? await loadGroupeNode(e.IDGRPFILS, profondeur + 1) : null,
                    CHAMPEV: e.CHAMPEV ?? null,
                    OPECOMP: e.OPECOMP ?? null,
                    VALEUR1: e.VALEUR1 ?? null,
                    VALEUR2: e.VALEUR2 ?? null,
                    LISTEVAL: e.LISTEVAL ?? null,
                });
            }
            return {
                IDGRP: g.IDGRP, CODGRP: g.CODGRP, LIBGRP: g.LIBGRP,
                LOGIQUE: g.LOGIQUE === 'OU' ? 'OU' : 'ET',
                ACTIVE: g.ACTIVE === true || g.ACTIVE === 1,
                ANONYME: g.ANONYME === true || g.ANONYME === 1,
                ROWVER: g.ROWVER,
                ELEMENTS: elements,
            };
        } catch (err) {
            console.error('Erreur chargement groupe:', err);
            return null;
        }
    };

    // Selection radio -> remplit le bloc detail ; un groupe charge son arbre
    const onSelectRow = async (row: RegleRow | null) => {
        setSelectedKey(row?._key ?? null);
        setSelectedRule(row);
        setGroupeTree(null);
        if (row?.TYPE === 'GROUPE' && row.IDGRP) {
            setDetailLoading(true);
            try { setGroupeTree(await loadGroupeNode(row.IDGRP)); }
            finally { setDetailLoading(false); }
        }
    };

    const openNew = () => {
        setEditingRule(null);
        setEditingTree(null);
        setModalOpen(true);
    };

    // Edition : un groupe est charge en arbre complet avant d'ouvrir la modale
    const openEdit = async (row: RegleRow) => {
        if (row.TYPE === 'GROUPE' && row.IDGRP) {
            setLoading(true);
            const tree = await loadGroupeNode(row.IDGRP);
            setLoading(false);
            if (!tree) {
                SweetAlert.fire("Erreur", "Impossible de charger le groupe " + row.CODE, "error");
                return;
            }
            setEditingTree(tree);
        } else {
            setEditingTree(null);
        }
        setEditingRule(row);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingRule(null);
        setEditingTree(null);
    };

    // Reselectionne la regle venant d'etre enregistree apres rechargement.
    // type 'AUTO' : le backend a determine le type final (SaveDefinition).
    // Retour a la page 1 : la regle creee est la plus recente (tri DATCRE DESC).
    const afterSaved = async (code: string, type: 'CONDITION' | 'GROUPE' | 'AUTO') => {
        const q = { ...query, page: 1 };
        setQuery(q);
        const merged = await loadRules(operateurs, q);
        void loadCatalogues();
        const row = type === 'AUTO'
            ? merged.find(r => r.CODE === code)
            : merged.find(r => r.CODE === code && r.TYPE === type);
        if (row) void onSelectRow(row);
    };

    // Point d'entree unique : le backend determine le type final
    // (condition vs groupe) a partir de la definition envoyee.
    const saveDefinition = async (payload: any, isEdit: boolean): Promise<SaveResult> => {
        try {
            const res = await axios({
                method: 'post',
                url: OpeApi.SaveDefinition,
                headers: {
                    'CypherDefinition': Crypter.encrypt(JSON.stringify(payload)),
                    'Authorization': authHeader(),
                    'Content-Type': 'application/json'
                }
            });
            const r = decrypt(res.data);
            if (r?.StatutCode === 200) {
                closeModal();
                SweetAlert.fire("Succès", r.Message || "La règle a été enregistrée.", "success");
                void afterSaved(payload.DEFINITION.CODGRP, 'AUTO');
                return { ok: true };
            }
            return { ok: false, message: r?.Message || "Erreur de validation", detail: typeof r?.Result === 'string' ? r.Result : undefined };
        } catch (err: any) {
            return { ok: false, message: "Erreur serveur", detail: err.message };
        }
    };

    // Validation a blanc : la procedure stockee statue sans rien ecrire.
    // Appelee par les modales de saisie (condition simple / sous-groupe)
    // pour prevenir l'erreur des le point de saisie ; la validation
    // complete a l'enregistrement reste inchangee.
    const prevaliderCondition = async (c: any): Promise<{ ok: boolean; message?: string }> => {
        try {
            const res = await axios({
                method: 'post',
                url: OpeApi.Prevalider,
                headers: {
                    'CypherCondition': Crypter.encrypt(JSON.stringify({
                        CHAMPEV: c.CHAMPEV ?? '', OPECOMP: c.OPECOMP ?? '',
                        VALEUR1: c.VALEUR1 ?? null, VALEUR2: c.VALEUR2 ?? null,
                        LISTEVAL: Array.isArray(c.LISTEVAL) ? c.LISTEVAL.join(';') : (c.LISTEVAL ?? null),
                    })),
                    'Authorization': authHeader(),
                    'Content-Type': 'application/json'
                }
            });
            const r = decrypt(res.data);
            return r?.StatutCode === 200
                ? { ok: true }
                : { ok: false, message: r?.Message || 'Condition invalide' };
        } catch {
            return { ok: false, message: 'Erreur serveur lors de la vérification' };
        }
    };

    // Simulateur : la definition + les valeurs de test sont evaluees par
    // le backend avec la meme semantique que le moteur (1/0/-1 par
    // feuille, agregation ternaire). Aucune ecriture.
    const simulerDefinition = async (def: any, valeurs: Record<string, string>): Promise<SimuResult> => {
        try {
            const res = await axios({
                method: 'post',
                url: OpeApi.Simuler,
                headers: {
                    'CypherSimulation': Crypter.encrypt(JSON.stringify({ DEFINITION: def, VALEURS: valeurs })),
                    'Authorization': authHeader(),
                    'Content-Type': 'application/json'
                }
            });
            const r = decrypt(res.data);
            if (r?.StatutCode === 200) {
                const rs = r.Result;
                return {
                    ok: true,
                    resultat: rs.RESULTAT,
                    detail: rs.DETAIL ?? undefined,
                    snap: rs.SNAP ?? [],
                };
            }
            return { ok: false, message: r?.Message || 'Simulation impossible' };
        } catch {
            return { ok: false, message: 'Erreur serveur lors de la simulation' };
        }
    };

    // Domaine de valeurs d'un champ evalue (picklist "Valeur") : resolu
    // cote serveur. Table vide ou erreur -> champ sans domaine, la
    // modale garde alors la saisie typee (nombre/date/texte libre).
    const readDomaine = async (codchamp: string): Promise<{ value: string; label: string }[]> => {
        try {
            const res = await axios.get(OpeApi.ReadDomaine, {
                headers: { 'CODCHAMP': codchamp, 'Authorization': authHeader() }
            });
            const r = decrypt(res.data);
            return (r?.Result?.Table ?? [])
                .filter((x: any) => x.COD != null)
                .map((x: any) => ({ value: String(x.COD).trim(), label: `${String(x.COD).trim()} — ${x.LIB}` }));
        } catch { return []; }
    };

    // Elements d'un arbre charge -> payload ELEMENTS pour SaveGroupe :
    // les sous-groupes nommes restent des references (IDGRPFILS), les fils
    // anonymes et les conditions inline sont renvoyes tels quels.
    const treeToElements = (els: ElementNode[]): any[] => els.map((e, i) => ({
        ORDRE: i + 1,
        CODCOND: e.CODCOND ?? null,
        IDGRPFILS: e.FILS?.CODGRP ? (e.IDGRPFILS ?? e.FILS.IDGRP) : (e.CODCOND ? null : (e.IDGRPFILS ?? null)),
        FILS: e.FILS && !e.FILS.CODGRP ? {
            IDGRP: e.FILS.IDGRP ?? 0, CODGRP: null, LIBGRP: null, ANONYME: true,
            LOGIQUE: e.FILS.LOGIQUE, ACTIVE: e.FILS.ACTIVE,
            ELEMENTS: treeToElements(e.FILS.ELEMENTS),
        } : null,
        CHAMPEV: (e as any).CHAMPEV ?? null,
        OPECOMP: (e as any).OPECOMP ?? null,
        VALEUR1: (e as any).VALEUR1 ?? null,
        VALEUR2: (e as any).VALEUR2 ?? null,
        LISTEVAL: (e as any).LISTEVAL ?? null,
    }));

    // Activation / desactivation rapide sur la regle selectionnee
    const toggleActive = async () => {
        const row = selectedRule;
        if (!row) return;
        const newActive = !row.ACTIVE;
        const rep = await SweetAlert.fire({
            title: "Confirmation",
            text: `Voulez-vous ${newActive ? 'activer' : 'désactiver'} ${row.TYPE === 'GROUPE' ? 'le groupe' : 'la condition'} ${row.CODE} ?`,
            icon: "question", showCancelButton: true,
            confirmButtonText: "Oui", cancelButtonText: "Non"
        });
        if (!rep.isConfirmed) return;
        setLoading(true);
        try {
            let r: any;
            if (row.TYPE === 'CONDITION') {
                const res = await axios({
                    method: 'put', url: OpeApi.UpdateCondition,
                    headers: {
                        'CypherCondition': Crypter.encrypt(JSON.stringify({ ...row.raw, ACTIVE: newActive, UTILIS: Perss.coduti })),
                        'Authorization': authHeader(), 'Content-Type': 'application/json'
                    }
                });
                r = decrypt(res.data);
            } else {
                const tree = await loadGroupeNode(row.IDGRP!);
                if (!tree) {
                    setLoading(false);
                    SweetAlert.fire("Erreur", "Impossible de charger le groupe " + row.CODE, "error");
                    return;
                }
                const res = await axios({
                    method: 'post', url: OpeApi.SaveGroupe,
                    headers: {
                        'CypherGroupe': Crypter.encrypt(JSON.stringify({
                            IDGRP: tree.IDGRP, CODGRP: tree.CODGRP, LIBGRP: tree.LIBGRP,
                            LOGIQUE: tree.LOGIQUE, ACTIVE: newActive, UTILIS: Perss.coduti,
                            ROWVER: tree.ROWVER, ELEMENTS: treeToElements(tree.ELEMENTS)
                        })),
                        'Authorization': authHeader(), 'Content-Type': 'application/json'
                    }
                });
                r = decrypt(res.data);
            }
            setLoading(false);
            if (r?.StatutCode === 200) {
                SweetAlert.fire("Succès", r.Message || "Statut mis à jour.", "success");
                void afterSaved(row.CODE, row.TYPE);
            } else {
                SweetAlert.fire(row.ACTIVE ? "Désactivation impossible" : "Activation impossible",
                    r?.Message || "Erreur lors du changement de statut.", "warning");
            }
        } catch (err: any) {
            setLoading(false);
            SweetAlert.fire("Erreur", "Détail technique : " + err.message, "error");
        }
    };

    // Annuler : deselectionne la regle ET reinitialise les filtres
    // du catalogue (type, statut, recherche) a "Tous".
    const filtresDefaut = query.type === 'TOUS' && query.statut === 'TOUS' && !query.search;
    const handleAnnul = () => {
        void onSelectRow(null);
        onQuery({ page: 1, type: 'TOUS', statut: 'TOUS', search: '' });
    };

    const columns: ColumnsType<RegleRow> = [
        {
            key: 'CODE', title: 'Code', dataIndex: 'CODE', width: 130,
            render: (v: string, r) => (
                <Tooltip title={r.LIBELLE} placement="top">
                    <span style={{ fontWeight: 'bold' }}>{v}</span>
                </Tooltip>
            )
        },
        {
            key: 'LIBELLE', title: 'Libellé', dataIndex: 'LIBELLE', width: 220,
            render: (v: string) => <div className="whitespace-normal break-words">{v}</div>
        },
        {
            key: 'TYPE', title: 'Type', width: 110, align: 'center',
            render: (_: any, r) => r.TYPE === 'GROUPE'
                ? <Tag color="green">Groupe</Tag>
                : <Tag color="blue">Unique</Tag>
        },
        {
            key: 'STATUT', title: 'Statut', width: 100, align: 'center',
            render: (_: any, r) => r.ACTIVE
                ? <Tag color="success">Activé</Tag>
                : <Tag color="default">Désactivé</Tag>
        },
        // Dernier acteur : UTILIS est mis a jour par le backend a chaque
        // creation/modification ; la date affiche DATMOD sinon DATCRE.
        // Survol du code -> nom complet (NOMUTI resolu cote backend).
        {
            key: 'SAISIPAR', title: 'Saisi par', width: 120,
            render: (_: any, r) => {
                const u = r.raw?.UTILIS;
                const nom = r.raw?.NOMUTI;
                const d = r.raw?.DATMOD || r.raw?.DATCRE;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Tooltip title={nom || 'Utilisateur inconnu'} placement="top">
                            <span style={{ fontSize: '0.8rem', cursor: 'default', color: '#116599', fontWeight: 600 }}>{u || '-'}</span>
                        </Tooltip>
                        <span style={{ fontSize: '0.65rem', color: 'gray' }}>
                            {d ? dayjs(d).format('DD/MM/YYYY HH:mm:ss') : '-'}
                        </span>
                    </Box>
                );
            }
        },
    ];

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span><UserOutlined /> Gestion des conditions</span>,
            children: <Tab1
                form={form}
                rules={rules}
                columns={columns}
                selectedKey={selectedKey}
                onSelectRow={onSelectRow}
                selectedRule={selectedRule}
                groupeTree={groupeTree}
                detailLoading={detailLoading}
                conditions={conditions}
                groupes={groupes}
                champs={champs}
                operateurs={operateurs}
                agences={agences}
                profMax={profMax}
                utilis={Perss.coduti}
                codage={Perss.codage}
                agence={Perss.agence}
                nomuti={Perss.nomuti}
                loading={loading}
                modalOpen={modalOpen}
                editingRule={editingRule}
                editingTree={editingTree}
                onCloseModal={closeModal}
                onSaveDefinition={saveDefinition}
                onPrevalider={prevaliderCondition}
                onSimuler={simulerDefinition}
                onReadDomaine={readDomaine}
                total={total}
                pageSize={PAGE_SIZE}
                query={query}
                onQuery={onQuery}
                onExport={handleExportRegles}
            />,
        },
    ];

    return (
        <div className='tab'>
            <Typography align='center' className='formTitle'><b>/PROFILGESTION/CONDITIONSOPERATIONS</b></Typography>
            <Spin spinning={loading} size='large'>
                <Form form={form} preserve labelAlign="left">
                    <div className='bG'>
                        <legend><Typography color={"#116599"}>Actions</Typography></legend>
                        <Form.Item className='bouttong'>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button size='large' onClick={openNew} block>Ajouter</Button>
                                <Button size='large' disabled={!selectedRule} onClick={() => selectedRule && openEdit(selectedRule)} block>Modifier</Button>
                                <Button size='large' disabled={!selectedRule} onClick={toggleActive} block>
                                    {selectedRule?.ACTIVE ? 'Désactiver' : 'Activer'}
                                </Button>
                                <Button size='large' disabled={loading} onClick={() => loadRules()} block>Actualiser</Button>
                                <Button size='large' disabled={!selectedRule && filtresDefaut} onClick={handleAnnul} block>Annuler</Button>
                            </Space>
                        </Form.Item>
                    </div>
                    <div className='clientP'>
                        <Tabs
                            size='large'
                            type="card"
                            items={items}
                        />
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default ConditionsOperations;
